/**
 * Format task start/due dates from API (DATE → "YYYY-MM-DD", TIMESTAMP → ISO string).
 * Avoids appending "T00:00:00" to values that already contain "T", which caused Invalid Date.
 */
export function formatTaskDate(value: string | null | undefined): string {
  if (value == null || String(value).trim() === '') return '—';
  const s = String(value).trim();
  let d: Date;
  // Calendar date: plain YYYY-MM-DD or ISO from Postgres DATE (e.g. …T00:00:00.000Z).
  // Parsing the full ISO string uses UTC midnight and shows as the previous day in US timezones.
  const cal = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (cal) {
    const y = parseInt(cal[1], 10);
    const mo = parseInt(cal[2], 10) - 1;
    const da = parseInt(cal[3], 10);
    d = new Date(y, mo, da, 12, 0, 0, 0);
  } else {
    d = new Date(s);
  }
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Normalize API date/timestamp to YYYY-MM-DD for <input type="date" />. */
export function toDateInputValue(value: string | null | undefined): string {
  if (value == null || String(value).trim() === '') return '';
  const s = String(value).trim();
  const dayPrefix = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dayPrefix) return dayPrefix[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

/** Local calendar start-of-day for comparing due dates (handles YYYY-MM-DD and ISO strings). */
function startOfLocalDayFromDueValue(value: string): Date | null {
  const s = String(value).trim();
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10) - 1;
    const da = parseInt(m[3], 10);
    return new Date(y, mo, da, 0, 0, 0, 0);
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/** True if task has a due date before today (local) and is not completed. */
export function isTaskOverdue(task: { due_date?: string | null; status?: string }): boolean {
  if (task.status === 'Completed' || task.status === 'Void' || task.status === 'On Hold') return false;
  const due = startOfLocalDayFromDueValue(task.due_date ?? '');
  if (!due) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
}
